import { NativeDraftStorage } from "@/native/nativeDraftStorage";
import { isRealNativePlatform } from "@/native/platform";
import {
  capDraft,
  VOICE_DRAFT_STORAGE_KEY,
  type DraftStorage,
  type VoiceDraftV1,
} from "./voiceDraft";

/**
 * Crash-safe persistence for the in-flight voice transcript.
 *
 * The controller writes a throttled draft snapshot while recording (plus
 * unthrottled flushes on commits, pauses, and app-backgrounding), so a force
 * kill or webview death loses at most the throttle window of *text* — audio
 * is never persisted. The draft is cleared ONLY on confirmed delivery to a
 * destination or an explicit user discard; the voice screen offers recovery
 * on next open when one is found.
 *
 * Native uses Capacitor Preferences (see native/nativeDraftStorage); web falls
 * back to localStorage.
 */

export {
  EphemeralDraftStorage,
  type DraftStorage,
  type VoiceDraftV1,
} from "./voiceDraft";

class WebDraftStorage implements DraftStorage {
  async load(): Promise<VoiceDraftV1 | null> {
    try {
      const value = localStorage.getItem(VOICE_DRAFT_STORAGE_KEY);
      return value ? (JSON.parse(value) as VoiceDraftV1) : null;
    } catch {
      return null;
    }
  }
  async save(draft: VoiceDraftV1): Promise<void> {
    try {
      localStorage.setItem(
        VOICE_DRAFT_STORAGE_KEY,
        JSON.stringify(capDraft(draft)),
      );
    } catch {
      // Quota — the recording continues; persistence is best-effort on web.
    }
  }
  async clear(): Promise<void> {
    try {
      localStorage.removeItem(VOICE_DRAFT_STORAGE_KEY);
    } catch {
      // Ignore.
    }
  }
}

export function createDraftStorage(): DraftStorage {
  return isRealNativePlatform()
    ? new NativeDraftStorage()
    : new WebDraftStorage();
}
